import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import JsonLd from "../components/seo/JsonLd";
import usePageSeo from "../hooks/usePageSeo";
import SiteOperatorLine from "../components/seo/SiteOperatorLine";
import { buildWebPageSchema } from "../data/seo/structuredData";

const TITLE = "Terms of Service — FloGPT";
const DESCRIPTION =
    "Terms for using FloGPT: acceptable use, accounts and security, intellectual property, disclaimers, termination, and how to contact legal@flogpt.com.";

export default function Terms() {
    usePageSeo({
        title: TITLE,
        description: DESCRIPTION,
        canonical: "/terms",
    });
    const webPageSchema = useMemo(
        () => buildWebPageSchema({ name: TITLE, description: DESCRIPTION, path: "/terms" }),
        [],
    );

    return (
        <div className="bg-slate-900 min-h-screen">
            <LandingNavbar />
            <JsonLd id="webpage-terms" data={webPageSchema} />
            <main className="pt-28">
            <div className="mx-auto max-w-4xl px-6 lg:px-8">
                <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 p-8">
                    <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-indigo-500/10 to-purple-500/10 blur-xl" />
                    <div className="relative">
                        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="text-4xl font-bold tracking-tight text-white">
                            Terms of Service
                        </motion.h1>
                        <p className="mt-3 text-slate-300">Last updated: 2025-10-28</p>

                        <div className="mt-8 space-y-8 text-slate-300 leading-relaxed">
                            <section>
                                <h2 className="text-xl font-semibold text-white">Acceptance of Terms</h2>
                                <p className="mt-2">By using FloGPT, you agree to these Terms. If you do not agree, do not use the Services.</p>
                            </section>
                            <section>
                                <h2 className="text-xl font-semibold text-white">Use of Service</h2>
                                <ul className="mt-2 list-disc pl-6 space-y-1">
                                    <li>Do not misuse or attempt to disrupt the service.</li>
                                    <li>You are responsible for content you upload.</li>
                                    <li>We may update or discontinue features with notice.</li>
                                </ul>
                            </section>
                            <section>
                                <h2 className="text-xl font-semibold text-white">Accounts and Security</h2>
                                <p className="mt-2">You are responsible for maintaining the confidentiality of your credentials and for all activities under your account. Notify us immediately of unauthorized use.</p>
                            </section>
                            <section>
                                <h2 className="text-xl font-semibold text-white">Intellectual Property</h2>
                                <p className="mt-2">The platform, including software and branding, is owned by FloGPT or its licensors. You receive a limited, non-exclusive, non-transferable license to use the Services.</p>
                            </section>
                            <section>
                                <h2 className="text-xl font-semibold text-white">Disclaimer</h2>
                                <p className="mt-2">Service is provided "as is" without warranties. To the maximum extent permitted by law, we are not liable for indirect, incidental, or consequential damages.</p>
                            </section>
                            <section>
                                <h2 className="text-xl font-semibold text-white">Termination</h2>
                                <p className="mt-2">We may suspend or terminate access for violations of these Terms or for security, legal, or operational reasons.</p>
                            </section>
                            <section>
                                <h2 className="text-xl font-semibold text-white">Governing Law</h2>
                                <p className="mt-2">
                                    These Terms are governed by applicable laws of your jurisdiction unless otherwise required. Disputes will be resolved in competent courts where we are domiciled, unless mandatory laws dictate otherwise.
                                </p>
                            </section>
                            <section>
                                <h2 className="text-xl font-semibold text-white">Contact</h2>
                                <p className="mt-2">
                                    Questions? Email legal@flogpt.com. See also the{" "}
                                    <Link to="/privacy" className="text-white underline">
                                        Privacy Policy
                                    </Link>
                                    .
                                </p>
                            </section>
                            <section className="pt-4 border-t border-slate-700">
                                <SiteOperatorLine className="text-sm font-medium text-slate-500" />
                            </section>
                        </div>
                    </div>
                </div>
            </div>
            </main>
            <Footer />
        </div>
    );
}
